import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, BatteryWarning, RefreshCw } from "lucide-react";
import BatteryHistoryChart from "./common/BatteryHistoryChart";
import { BATTERY_LOG_FW_LABEL } from "utils/batteryLog";

/**
 * Battery tab of the USB device screen: reads the wearable's battery log
 * (`battery`, firmware v0.17.0) and draws it. The read itself lives in the
 * modal (useBatteryLog) so the result survives tab switches.
 *
 * batteryLog  useBatteryLog() result
 * disabled    the console is busy or locked — don't send now
 * progress    lines received so far while loading
 */
const BatteryHistoryPanel = ({ batteryLog, disabled, progress = 0 }) => {
  const { t } = useTranslation();
  const { status, readings, header, complete, error, load } = batteryLog;

  // First visit reads it; after that it's the Refresh button.
  useEffect(() => {
    if (status === "idle" && !disabled) load();
  }, [status, disabled, load]);

  const loading = status === "loading" || status === "idle";
  const total = header?.n;

  return (
    <div className="rounded-xl border border-[#EDEDEA] bg-white px-5 py-4 dark:border-[#1F2937] dark:bg-[#111827]">
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-[#111827] dark:text-[#F9FAFB]">
            {t("devices.batteryHistory.title")}
          </h3>
          <p className="mt-0.5 text-[12px] text-[#6B7280] dark:text-[#9CA3AF]">
            {status === "ready" && total != null
              ? t("devices.batteryHistory.summary", {
                  count: readings.length,
                  minutes: Math.round((header.interval_s || 60) / 60),
                })
              : t("devices.batteryHistory.subtitle")}
          </p>
        </div>
        <button
          onClick={load}
          disabled={disabled || status === "loading"}
          className="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-[12px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-40"
        >
          <RefreshCw
            size={12}
            className={status === "loading" ? "animate-spin" : ""}
          />
          {t("common.actions.refresh")}
        </button>
      </div>

      {status === "needsFirmware" ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#E5E7EB] px-6 py-10 text-center">
          <BatteryWarning size={20} className="text-[#9CA3AF]" />
          <p className="text-[13px] font-medium text-[#374151]">
            {t("devices.batteryHistory.needsFirmware", {
              version: BATTERY_LOG_FW_LABEL,
            })}
          </p>
          <p className="max-w-sm text-[12px] text-[#9CA3AF]">
            {t("devices.batteryHistory.needsFirmwareHint")}
          </p>
        </div>
      ) : (
        <>
          {status === "error" && (
            <div className="mb-3 flex items-center gap-2 rounded-lg bg-[#FEF2F2] px-3 py-2 text-[12px] text-[#B91C1C]">
              <AlertCircle size={14} className="flex-shrink-0" />
              {t("devices.batteryHistory.failed", { message: error })}
            </div>
          )}
          {status === "ready" && !complete && (
            <div className="mb-3 flex items-center gap-2 rounded-lg bg-[#FEF3E7] px-3 py-2 text-[12px] text-[#9A4A17]">
              <AlertCircle size={14} className="flex-shrink-0" />
              {t("devices.batteryHistory.incomplete", {
                got: readings.length,
                total: total ?? "?",
              })}
            </div>
          )}
          <BatteryHistoryChart
            readings={readings}
            intervalS={header?.interval_s || 60}
            loading={loading && !readings.length}
            loadingText={
              status === "loading" && progress > 0
                ? t("devices.batteryHistory.reading", { count: progress })
                : undefined
            }
            emptyText={t("devices.batteryHistory.empty")}
          />
        </>
      )}
    </div>
  );
};

export default BatteryHistoryPanel;
